// @ts-nocheck
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import EmailBuilder from './EmailBuilder';
import { renderEmail } from './emailBlocks';
import { translateItemToAllLanguages } from './services/apiService';
import { loadPdfFonts } from './loadPdfFonts';
import { generateBoardingPass } from './services/boardingPassService';
import { getBooking, getAllBookings } from './services/apiService';
import { mapBookingFields, findBookingByCode, fillPlaceholders, todayISO } from './services/bookingFields';
import { getTranslations } from './translations';

const API = 'https://yachtmanagementsuite.com/api';
const DOC_LANGS = ['el', 'en', 'it', 'de', 'ru', 'fr', 'ro', 'pl', 'he', 'es'];
const PH_RE_TEST = /\{\{[A-Za-z0-9_]+\}\}/;
const LANG_FLAGS = { el: '🇬🇷', en: '🇬🇧', it: '🇮🇹', de: '🇩🇪', ru: '🇷🇺', fr: '🇫🇷', ro: '🇷🇴', pl: '🇵🇱', he: '🇮🇱', es: '🇪🇸' };

export default function DocumentsManager() {
  const navigate = useNavigate();
  const language = sessionStorage.getItem('yacht_lang') || 'en';   // ίδιο pattern με HomePage
  const t = getTranslations(language);
  const [view, setView] = useState('LIST');            // 'LIST' | 'EDITOR'
  const [docs, setDocs] = useState([]);
  const [cardLang, setCardLang] = useState({});
  const [selected, setSelected] = useState(null);       // doc | null (null = νέο/άδειο)
  const [builderBlocks, setBuilderBlocks] = useState([]); // master el blocks
  const [builderKey, setBuilderKey] = useState(0);      // remount counter
  const [editName, setEditName] = useState('');
  const [translations, setTranslations] = useState({}); // {lang:{blockId:{text?,label?}}}
  const [previewLang, setPreviewLang] = useState('el');
  const [busy, setBusy] = useState(false);
  const [bpQuery, setBpQuery] = useState('');
  const [bpBooking, setBpBooking] = useState(null);
  const [bpMsg, setBpMsg] = useState('');
  const isStaffUser = (() => {
    try {
      const u = JSON.parse(sessionStorage.getItem('auth_current_user') || 'null');
      return !!(u && (u.role === 'OWNER' || u.role === 'ADMIN' || u.role === 'EMPLOYEE' || u.role === 'employee'));
    } catch { return false; }
  })();

  // ── API HELPERS ──────────────────────────────────
  const loadList = useCallback(async () => {
    try {
      const r = await fetch(`${API}/documents.php?company_id=1`);
      const json = await r.json();
      const list = Array.isArray(json) ? json : [];
      const full = await Promise.all(list.map(async (d) => {
        try {
          const rr = await fetch(`${API}/documents.php?company_id=1&id=${d.id}`);
          const fd = await rr.json();
          return fd && fd.id ? fd : d;
        } catch { return d; }
      }));
      setDocs(full.filter(d => (d.name || '') !== 'Blank / Free Build'));
    } catch (e) {}
  }, []);

  async function openDoc(id) {
    try {
      const r = await fetch(`${API}/documents.php?company_id=1&id=${id}`);
      const full = await r.json();
      if (!full) { window.alert('Δεν βρέθηκε / Not found'); return; }
      let bl = [];
      try { bl = JSON.parse(full.blocks_json || '[]'); } catch { bl = []; }
      let tr = {};
      try { tr = JSON.parse(full.translations_json || '{}'); } catch { tr = {}; }
      setBuilderBlocks(Array.isArray(bl) ? bl : []);
      setTranslations(tr && typeof tr === 'object' ? tr : {});
      setEditName(full.name || '');
      setSelected(full);
      setPreviewLang('el');
      setBuilderKey(k => k + 1);
      setView('EDITOR');
    } catch (e) { window.alert('Σφάλμα / Error'); }
  }

  function newBlank() {
    setSelected(null);
    setBuilderBlocks([]);
    setTranslations({});
    setEditName('');
    setPreviewLang('el');
    setBuilderKey(k => k + 1);
    setView('EDITOR');
  }

  async function saveDoc(blocks, _html, name) {
    const body = {
      name: name || editName || 'Untitled',
      doc_type: selected?.doc_type || 'custom',
      blocks_json: JSON.stringify(blocks || []),
      translations_json: JSON.stringify(translations || {}),
    };
    try {
      if (selected?.id) {
        body.id = selected.id;
        await fetch(`${API}/documents.php?company_id=1`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
        });
      } else {
        await fetch(`${API}/documents.php?company_id=1`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
        });
      }
      await loadList();
      setView('LIST');
    } catch (e) { window.alert('Σφάλμα αποθήκευσης / Save error'); }
  }

  async function deleteDoc(id) {
    if (!window.confirm('Διαγραφή εγγράφου;')) return;
    try {
      await fetch(`${API}/documents.php?company_id=1&id=${id}`, { method: 'DELETE' });
      await loadList();
    } catch (e) {}
  }

  // ── ΜΕΤΑΦΡΑΣΗ 10 γλωσσών ─────────────────────────
  async function translateAll() {
    setBusy(true);
    try {
      const next = {};
      for (const block of builderBlocks) {
        const src = block.props?.text ?? block.props?.label ?? '';
        if (!src) continue;
        const field = block.props?.label != null ? 'label' : 'text';
        const r = await translateItemToAllLanguages(src, src); // el→8 (en fallback στο master)
        for (const lang of ['it', 'de', 'ru', 'fr', 'ro', 'pl', 'he', 'es']) {
          next[lang] = next[lang] || {};
          next[lang][block.id] = { [field]: r[lang] };
        }
      }
      setTranslations(next);
      if (selected?.id) {
        const body = {
          id: selected.id,
          name: editName || selected.name || 'Untitled',
          doc_type: selected.doc_type || 'custom',
          blocks_json: JSON.stringify(builderBlocks || []),
          translations_json: JSON.stringify(next),
        };
        await fetch(`${API}/documents.php?company_id=1`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
        });
      }
      window.alert('Μετάφραση ολοκληρώθηκε (8 γλώσσες + Ελληνικά master).');
    } catch (e) {
      window.alert('Σφάλμα μετάφρασης: ' + (e?.message || e));
    } finally {
      setBusy(false);
    }
  }

  // ── PLACEHOLDER FILL ─────────────────────────────
  // Πεδία του επιλεγμένου ναύλου. ΙΔΙΑ πηγή με το boarding pass: το bpBooking
  // που γεμίζει ο picker (getAllBookings + codeMatches). Ένας picker, δύο καταναλωτές.
  const bpFields = React.useMemo(
    () => mapBookingFields(bpBooking) || { signDate: todayISO() },
    [bpBooking]
  );

  // Αντικατάσταση {{...}} σε όλα τα κειμενικά props, ΠΡΙΝ από κάθε render/PDF.
  // Χωρίς επιλεγμένο ναύλο → κενό, ΠΟΤΕ ορατό {{...}} σε πελάτη.
  function fillBlocks(blocks) {
    return (blocks || []).map(b => {
      if (!b) return b;
      // Έγγραφα σπαρμένα με flat σχήμα {id,text,type} δεν έχουν props: χωρίς
      // normalize έμεναν ΚΕΝΑ όπου δεν υπάρχει overlay μετάφρασης να τα γεμίσει,
      // και το κείμενό τους δεν περνούσε ποτέ από fillPlaceholders.
      const p = { ...(b.props || {}) };
      for (const k of ['text', 'label', 'title']) {
        if (p[k] == null && typeof b[k] === 'string') p[k] = b[k];
        if (typeof p[k] === 'string') p[k] = fillPlaceholders(p[k], bpFields);
      }
      return { ...b, props: p };
    });
  }

  // ── PREVIEW ANA ΓΛΩΣΣΑ ───────────────────────────
  function blocksForLang(lang) {
    if (!lang) return builderBlocks;   // ωμά — ο EmailBuilder ΔΕΝ πρέπει να δει γεμισμένο κείμενο
    const merged = (builderBlocks || []).map(b => {
      const ov = translations?.[lang]?.[b.id];
      return ov ? { ...b, props: { ...b.props, ...ov } } : b; // χωρίς overlay → master (fallback)
    });
    return fillBlocks(merged);
  }

  // ── EXPORT ───────────────────────────────────────
  // mapBookingForBP() μετακόμισε στο services/bookingFields.ts ως mapBookingFields()
  // — κοινή με το sea scooter terms placeholder fill. Ίδια πηγή, ίδια πεδία.
  async function bpSearch() {
    const q = (bpQuery || '').trim();
    if (!q) { setBpMsg('Δώσε αριθμό ναύλου.'); return; }
    setBusy(true); setBpMsg('Αναζήτηση...');
    try {
      const all = await getAllBookings();
      const found = findBookingByCode(all, q);
      if (!found) { setBpBooking(null); setBpMsg('Δεν βρέθηκε ναύλος: ' + q); }
      else { setBpBooking(found); setBpMsg('Βρέθηκε: ' + (found.bookingCode || found.code || found.bookingNumber)); }
    } catch (e) { setBpBooking(null); setBpMsg('Σφάλμα αναζήτησης.'); }
    setBusy(false);
  }
  function bpDownload(previewOnly) {
    if (!bpBooking) { setBpMsg('Πρώτα βρες ναύλο.'); return; }
    try { generateBoardingPass(mapBookingFields(bpBooking), { previewOnly: !!previewOnly }); }
    catch (e) { setBpMsg('Σφάλμα παραγωγής PDF.'); }
  }
  function docBlocksForLang(docObj, lang) {
    let bl = [];
    try { bl = JSON.parse(docObj.blocks_json || '[]'); } catch { bl = []; }
    if (!lang) return bl;
    let tr = {};
    try { tr = JSON.parse(docObj.translations_json || '{}'); } catch { tr = {}; }
    const merged = (bl || []).map(b => {
      const ov = tr?.[lang]?.[b.id];
      return ov ? { ...b, props: { ...b.props, ...ov } } : b;
    });
    return fillBlocks(merged);
  }
  async function exportDocPdf(docMeta, previewOnly, lang) {
    try {
      const r = await fetch(`${API}/documents.php?company_id=1&id=${docMeta.id}`);
      const full = await r.json();
      const useLang = lang || 'el';
      let blks = docBlocksForLang(full, useLang);
      await loadPdfFonts();
      const jsPDF = (window as any).jspdf?.jsPDF;
      if (!jsPDF) { alert('PDF engine δεν φορτώθηκε'); return; }
      const doc = new jsPDF();
      try {
        doc.addFileToVFS('DejaVuSans.ttf', (window as any).DejaVuSansBase64);
        doc.addFont('DejaVuSans.ttf', 'DejaVuSans', 'normal');
        doc.addFileToVFS('DejaVuSans-Bold.ttf', (window as any).DejaVuSansBoldBase64);
        doc.addFont('DejaVuSans-Bold.ttf', 'DejaVuSans', 'bold');
        doc.setFont('DejaVuSans');
      } catch (e) {}
      let y = 18;
      (blks || []).forEach(b => {
        const t = (b.props && (b.props.text || b.props.label || b.props.title)) || '';
        if (!t) return;
        const size = b.type === 'headline' ? 16 : 11;
        doc.setFontSize(size);
        const lines = doc.splitTextToSize(String(t), 180);
        lines.forEach(ln => {
          if (y > 280) { doc.addPage(); y = 18; }
          doc.text(ln, 15, y); y += size * 0.6 + 2;
        });
        y += 3;
      });
      if (previewOnly) { window.open(doc.output('bloburl'), '_blank'); }
      else { doc.save(`${full.name || 'document'}.pdf`); }
    } catch (e) { alert('Σφάλμα PDF.'); }
  }
  // ── GUIDE (rawHTML) — iframe preview + άθικτο .html download ──
  function previewGuideHtml(d) {
    try {
      const blob = new Blob([d.html_content || ''], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');                       // blob URL → scripts τρέχουν → ζωντανός Leaflet χάρτης
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { window.alert(t.error); }
  }
  function downloadGuideHtml(d) {
    try {
      const blob = new Blob([d.html_content || ''], { type: 'text/html;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = ((d.name || 'guide').replace(/[\/\\]/g, '-')) + '.html';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) { window.alert(t.error); }
  }

  async function exportPdf() {
    await loadPdfFonts();
    const jsPDF = (window as any).jspdf?.jsPDF;
    if (!jsPDF) { window.alert('PDF engine δεν φορτώθηκε'); return; }
    const doc = new jsPDF();
    try {
      doc.addFileToVFS('DejaVuSans.ttf', (window as any).DejaVuSansBase64);
      doc.addFont('DejaVuSans.ttf', 'DejaVuSans', 'normal');
      doc.addFileToVFS('DejaVuSans-Bold.ttf', (window as any).DejaVuSansBoldBase64);
      doc.addFont('DejaVuSans-Bold.ttf', 'DejaVuSans', 'bold');
    } catch (e) {}
    doc.setFont('DejaVuSans');
    const blocks = blocksForLang(previewLang);
    let y = 20;
    const lineHeight = 8;
    for (const b of blocks) {
      const txt = b.props?.text ?? b.props?.label ?? '';
      if (!txt) continue;
      const lines = doc.splitTextToSize(String(txt), 180);
      for (const ln of lines) {
        if (y > 270) { doc.addPage(); y = 20; }
        doc.text(ln, 15, y);
        y += lineHeight;
      }
      y += 2;
    }
    doc.save(`${editName || 'document'}-${previewLang}.pdf`);
  }

  function exportWord() { window.alert('Word export: σύντομα (Phase 2)'); }            // STUB
  function sendEmail() { window.alert('Αποστολή ως attachment: σύντομα — θα συνδεθεί με το email-send στο επόμενο βήμα'); } // STUB

  useEffect(() => { loadList(); }, [loadList]);

  // Auto-load boarding pass του συνδεδεμένου πελάτη (context → ?booking= → localStorage). ΜΟΝΟ ανάγνωση.
  useEffect(() => {
    (async () => {
      try {
        const urlBooking = new URLSearchParams(window.location.search).get('booking');
        const raw = urlBooking || sessionStorage.getItem('currentBooking') || localStorage.getItem('currentBooking') || '';
        if (!String(raw).trim()) return;
        const all = await getAllBookings();
        const found = findBookingByCode(all, String(raw).trim());
        if (found) { setBpBooking(found); setBpMsg(t.boardingPassReady); }
      } catch (e) {}
    })();
  }, []);

  // ── STYLES ───────────────────────────────────────
  const btn = { padding: '8px 14px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', fontSize: 14 };
  const btnPrimary = { ...btn, background: '#1e88e5', color: '#fff', border: 'none', fontWeight: 'bold' };

  // ── EDITOR VIEW ──────────────────────────────────
  if (view === 'EDITOR') {
    return (
      <div style={{ minHeight: '100vh', background: '#f1f5f9' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 12, background: '#fff', borderBottom: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
          <button style={btn} onClick={() => setView('LIST')}>← Πίσω</button>
          <input
            value={editName}
            onChange={e => setEditName(e.target.value)}
            placeholder="Όνομα εγγράφου"
            style={{ ...btn, minWidth: 200, cursor: 'text' }}
          />
          <button style={btnPrimary} onClick={() => saveDoc(builderBlocks, '', editName)}>💾 Αποθήκευση</button>
          <button style={btn} disabled={busy} onClick={translateAll}>{busy ? '… Μετάφραση' : '🌍 Μετάφραση 10 γλωσσών'}</button>
          <button style={btn} onClick={exportPdf}>PDF</button>
          <button style={btn} onClick={exportWord}>Word</button>
          <button style={btn} onClick={sendEmail}>📧 Email</button>
        </div>

        <div style={{ display: 'flex', gap: 6, padding: 10, flexWrap: 'wrap', background: '#fff', borderBottom: '1px solid #e2e8f0' }}>
          {DOC_LANGS.map(lang => (
            <button
              key={lang}
              onClick={() => setPreviewLang(lang)}
              style={{
                ...btn, fontSize: 20, padding: '4px 10px',
                ...(lang === previewLang ? { background: '#dbeafe', border: '2px solid #1e88e5' } : {}),
              }}
              title={lang}
            >
              {LANG_FLAGS[lang]}
            </button>
          ))}
        </div>

        {previewLang === 'el' ? (
          <EmailBuilder
            key={builderKey}
            initialBlocks={builderBlocks}
            templateName={'Blank / Free Build'}
            onBack={() => setView('LIST')}
            onSaveBlocks={saveDoc}
            gallery={[]} onUpload={() => {}} onLoadGallery={() => {}} onDeletePhoto={() => {}}
            templates={[]} onLoadTemplates={() => {}} onPickTemplate={() => {}} onNewBlank={newBlank}
            contacts={[]} onLoadContacts={() => {}} onAddContact={() => {}} onDeleteContact={() => {}}
            recipients={[]} onLoadRecipients={() => {}} onSend={() => {}}
          />
        ) : (
          <div style={{ maxWidth: 700, margin: '0 auto', padding: 16 }}>
            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 8 }}>
              Μετάφραση — read-only. Επεξεργασία στα Ελληνικά (master).
            </div>
            <div style={{ background: '#fff', borderRadius: 8, padding: 12 }} dangerouslySetInnerHTML={{ __html: renderEmail(blocksForLang(previewLang)) }} />
          </div>
        )}
      </div>
    );
  }

  // ── LIST VIEW ────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', background: '#f1f5f9', padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h1 style={{ fontSize: 24, fontWeight: 'bold', color: '#0d2840', margin: 0 }}>{t.documents}</h1>
        <button style={btn} onClick={() => navigate('/home')}>← {t.home}</button>
      </div>
      <div style={{ marginBottom: 18, padding: 14, background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0' }}>
        <p style={{ fontSize: 14, fontWeight: 700, color: '#0d2840', margin: '0 0 8px' }}>🔍 Επιλογή ναύλου — Boarding Pass &amp; έγγραφα με στοιχεία ναύλου</p>
        {isStaffUser && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input value={bpQuery} onChange={e => setBpQuery(e.target.value)} placeholder="Αριθμός ναύλου (π.χ. 189)"
            style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, minWidth: 200 }} />
          <button onClick={bpSearch} disabled={busy}
            style={{ padding: '8px 14px', background: '#1e88e5', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}>🔍 Αναζήτηση</button>
        </div>
        )}
        {bpMsg && <p style={{ fontSize: 12, color: '#64748b', margin: '8px 0 0' }}>{bpMsg}</p>}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
        {bpBooking && (
          <div style={{ background: '#fff', borderRadius: 10, padding: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontWeight: 'bold', color: '#0d2840', marginBottom: 8, fontSize: 15 }}>🪪 Boarding Pass</div>
            <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, background: '#f8fafc', padding: 12, marginBottom: 10, fontSize: 12, lineHeight: 1.8, color: '#334155' }}>
              <div>⛵ {bpBooking.vesselName || bpBooking.boatName || (bpBooking._sync && bpBooking._sync.vesselName) || '—'}</div>
              <div>👤 {bpBooking.clientName || `${(bpBooking._sync && bpBooking._sync.chartererFirstName) || bpBooking.skipperFirstName || ''} ${(bpBooking._sync && bpBooking._sync.chartererLastName) || bpBooking.skipperLastName || ''}`.trim() || '—'}</div>
              <div>📅 {bpBooking.checkInDate || bpBooking.startDate || (bpBooking._sync && bpBooking._sync.startDate) || '—'}</div>
              <div>📅 {bpBooking.checkOutDate || bpBooking.endDate || (bpBooking._sync && bpBooking._sync.endDate) || '—'}</div>
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 'auto' }}>
              <button onClick={() => bpDownload(true)} title={t.preview} style={{ flex: 1, padding: '6px 0', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', fontSize: 15 }}>👁 {t.preview}</button>
              <button onClick={() => bpDownload(false)} title={t.download} style={{ flex: 1, padding: '6px 0', borderRadius: 8, border: 'none', background: '#1e88e5', color: '#fff', cursor: 'pointer', fontSize: 15, fontWeight: 'bold' }}>📥 {t.download}</button>
            </div>
          </div>
        )}
        {docs.map(d => {
          const lang = cardLang[d.id] || 'el';
          const thumbBlocks = docBlocksForLang(d, lang);
          const isRtl = lang === 'he';
          const isGuide = !!(d.html_content && String(d.html_content).trim());
          return (
          <div key={d.id} style={{ background: '#fff', borderRadius: 10, padding: 14, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontWeight: 'bold', color: '#0d2840', marginBottom: 8, wordBreak: 'break-word', fontSize: 15 }}>📄 {d.name}</div>
            {PH_RE_TEST.test(String(d.blocks_json || '') + String(d.translations_json || '')) && (
              <div style={{ fontSize: 11, marginBottom: 6, padding: '3px 6px', borderRadius: 4,
                background: bpBooking ? '#dcfce7' : '#fef9c3', color: bpBooking ? '#166534' : '#854d0e' }}>
                {bpBooking
                  ? `✓ ${bpFields.bookingCode || '—'} · ${bpFields.clientName || '—'}`
                  : '⚠ Διάλεξε ναύλο παραπάνω'}
              </div>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
              {DOC_LANGS.map(lg => (
                <button key={lg} onClick={() => setCardLang(prev => ({ ...prev, [d.id]: lg }))}
                  title={lg}
                  style={{ fontSize: 15, lineHeight: 1, padding: '2px 4px', border: lg === lang ? '2px solid #1e88e5' : '1px solid #e2e8f0', borderRadius: 5, background: lg === lang ? '#dbeafe' : '#fff', cursor: 'pointer' }}>
                  {LANG_FLAGS[lg]}
                </button>
              ))}
            </div>
            <div style={{ height: 150, overflow: 'hidden', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', marginBottom: 10, position: 'relative' }}>
              {isGuide ? (
                // sandbox="" → scripts ΔΕΝ τρέχουν στο thumbnail (γρήγορο, styled preview χωρίς φόρτωση χάρτη)
                <iframe title={d.name} srcDoc={d.html_content} sandbox=""
                  style={{ border: 0, width: '250%', height: 375, transform: 'scale(0.4)', transformOrigin: 'top left', pointerEvents: 'none' }} />
              ) : (
                <div dir={isRtl ? 'rtl' : 'ltr'} style={{ transform: 'scale(0.4)', transformOrigin: 'top left', width: '250%', pointerEvents: 'none' }}
                  dangerouslySetInnerHTML={{ __html: renderEmail(thumbBlocks) }} />
              )}
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 'auto' }}>
              <button onClick={() => isGuide ? previewGuideHtml(d) : exportDocPdf(d, true, lang)} title={t.preview} style={{ flex: 1, padding: '6px 0', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', fontSize: 15 }}>👁 {t.preview}</button>
              <button onClick={() => isGuide ? downloadGuideHtml(d) : exportDocPdf(d, false, lang)} title={t.download} style={{ flex: 1, padding: '6px 0', borderRadius: 8, border: 'none', background: '#1e88e5', color: '#fff', cursor: 'pointer', fontSize: 15, fontWeight: 'bold' }}>📥 {t.download}</button>
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
}
